import type { ContactWireInput } from '@/lib/form-schema';

const REQUEST_TIMEOUT_MS = 5_000;

type FieldError = {
  field: 'name' | 'email' | 'message' | 'submissionId';
  message: string;
};

type FormError = {
  code: 'malformed_json' | 'invalid_form';
  message: string;
};

export type ContactApiResult =
  | { type: 'accepted'; submissionId: string }
  | {
      type: 'validation_error';
      fieldErrors: FieldError[];
      formErrors: FormError[];
    }
  | { type: 'challenge_required'; message: string }
  | { type: 'verification_failed'; message: string }
  | { type: 'request_rejected'; message: string }
  | { type: 'method_not_allowed'; message: string }
  | { type: 'payload_too_large'; message: string }
  | { type: 'unsupported_media_type'; message: string }
  | { type: 'rate_limited'; message: string; retryAfterSeconds: number }
  | { type: 'delivery_error'; message: string; retryable: boolean }
  | {
      type: 'provider_unavailable';
      message: string;
      provider?: 'verification' | 'delivery';
    }
  | { type: 'internal_error'; message: string }
  | { type: 'network_error' }
  | { type: 'unknown_error' };

async function parseJsonBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export async function submitContactInquiry(
  payload: ContactWireInput,
  options?: { signal?: AbortSignal }
): Promise<ContactApiResult> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    REQUEST_TIMEOUT_MS
  );

  const abortFromParent = () => controller.abort();
  options?.signal?.addEventListener('abort', abortFromParent, { once: true });

  try {
    const response = await fetch('/api/contact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    const body = await parseJsonBody(response);
    if (!isRecord(body) || typeof body.status !== 'string') {
      return { type: 'unknown_error' };
    }

    switch (response.status) {
      case 200: {
        if (
          body.status === 'accepted' &&
          typeof body.submissionId === 'string' &&
          body.submissionId === payload.submissionId
        ) {
          return { type: 'accepted', submissionId: body.submissionId };
        }
        return { type: 'unknown_error' };
      }
      case 400: {
        if (body.status !== 'validation_error') {
          return { type: 'unknown_error' };
        }
        return {
          type: 'validation_error',
          fieldErrors: Array.isArray(body.fieldErrors)
            ? (body.fieldErrors as FieldError[])
            : [],
          formErrors: Array.isArray(body.formErrors)
            ? (body.formErrors as FormError[])
            : [],
        };
      }
      case 403: {
        if (body.status === 'challenge_required') {
          return {
            type: 'challenge_required',
            message:
              typeof body.message === 'string'
                ? body.message
                : 'Additional verification is required.',
          };
        }
        if (body.status === 'verification_failed') {
          return {
            type: 'verification_failed',
            message:
              typeof body.message === 'string'
                ? body.message
                : 'Verification could not be completed.',
          };
        }
        if (body.status === 'request_rejected') {
          return {
            type: 'request_rejected',
            message:
              typeof body.message === 'string'
                ? body.message
                : 'This request was rejected.',
          };
        }
        return { type: 'unknown_error' };
      }
      case 405:
        return {
          type: 'method_not_allowed',
          message:
            typeof body.message === 'string'
              ? body.message
              : 'Method not allowed.',
        };
      case 413:
        return {
          type: 'payload_too_large',
          message:
            typeof body.message === 'string'
              ? body.message
              : 'Request body is too large.',
        };
      case 415:
        return {
          type: 'unsupported_media_type',
          message:
            typeof body.message === 'string'
              ? body.message
              : 'Unsupported media type.',
        };
      case 429: {
        const retryAfterSeconds =
          typeof body.retryAfterSeconds === 'number'
            ? body.retryAfterSeconds
            : Number(response.headers.get('Retry-After') ?? '60');
        return {
          type: 'rate_limited',
          message:
            typeof body.message === 'string'
              ? body.message
              : 'Too many submissions.',
          retryAfterSeconds: Math.max(1, retryAfterSeconds),
        };
      }
      case 502:
        return {
          type: 'delivery_error',
          message:
            typeof body.message === 'string'
              ? body.message
              : 'Delivery failed.',
          retryable: body.retryable !== false,
        };
      case 503:
        return {
          type: 'provider_unavailable',
          message:
            typeof body.message === 'string'
              ? body.message
              : 'Provider unavailable.',
          provider:
            body.provider === 'verification' || body.provider === 'delivery'
              ? body.provider
              : undefined,
        };
      case 500:
        return {
          type: 'internal_error',
          message:
            typeof body.message === 'string' ? body.message : 'Internal error.',
        };
      default:
        return { type: 'unknown_error' };
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return { type: 'network_error' };
    }
    return { type: 'network_error' };
  } finally {
    window.clearTimeout(timeoutId);
    options?.signal?.removeEventListener('abort', abortFromParent);
  }
}
